import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import RenderHtml from "../../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import ChildrenRenderer from "../../../../Documents/components/Nodes/ChildrenRenderer";

const PreviewStep = ({ version_content }) => {
  const {
    pageTitle,
    section,
    short_description,
    BOM_table_for_step_title,
    part_number_column_title,
    past_step_data_json,
  } = version_content;

  const dispatch = useDispatch();

  // Get step details from past_step_data_json
  const stepDetails = past_step_data_json?.step_details || [];
  const bomRows = past_step_data_json?.bom_rows || [];
  const bomTableTitleInfo = past_step_data_json?.bom_table_title_info;
  const partNumberColumnTitleInfo = past_step_data_json?.part_number_column_title_ref_info;

  return (
    <div>
      <div style={{ padding: "20px" }}>
        {/* Step Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px",
            paddingBottom: "20px",
            borderBottom: "3px double #e0e0e0",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input type="checkbox" defaultChecked={true} readOnly disabled />
            <h2 style={{ margin: 0, fontSize: "23px" }}>{pageTitle || "Step"}</h2>
          </div>
          <div
            style={{ color: "#888", fontSize: "14px", fontWeight: "normal" }}
          >
            {section} &gt; {pageTitle}
          </div>
        </div>

        {/* Step Description */}
        {short_description && (
          <p style={{ marginLeft: "5px", whiteSpace: "pre-wrap" }}>
            {short_description}
          </p>
        )}

        {/* Step Details */}
        {stepDetails && stepDetails.length > 0 && (
          <div style={{ marginLeft: "5px" }}>
            {structuredClone(stepDetails)
              .sort((a, b) => {
                // Check if orders field exists
                const hasOrdersA = a.hasOwnProperty("orders");
                const hasOrdersB = b.hasOwnProperty("orders");

                // If both have orders, sort by orders first, then created_at
                if (hasOrdersA && hasOrdersB) {
                  const orderA = parseInt(a.orders ?? 0, 10);
                  const orderB = parseInt(b.orders ?? 0, 10);
                  if (orderA !== orderB) return orderA - orderB;
                  // Fallback to created_at if orders are equal
                  return (
                    new Date(a.created_at || 0) - new Date(b.created_at || 0)
                  );
                }

                // If orders field is missing, sort by created_at in ascending order
                return (
                  new Date(a.created_at || 0) - new Date(b.created_at || 0)
                );
              })
              .map((myStep, index) => {
                const partNumber = myStep.partNumber || "";
                const description = myStep.description || "";
                const qtyText =
                  myStep.qty && myStep.qty != 0 ? `Qty ${myStep.qty}` : "";
                const partText = partNumber ? ` (${partNumber})` : "";
                return (
                  <span key={`step-detail-${index}`}>
                    {myStep.action && <>{myStep.action} </>}
                    {partText && <>{partText} </>}
                    {description && (
                      <>
                        <RenderHtml content={description} />{" "}
                      </>
                    )}
                    {qtyText && <>{qtyText}</>}&nbsp;
                  </span>
                );
              })}
          </div>
        )}

        {/* Parts Required Table (BOM Table) */}
        {bomRows && bomRows.length > 0 && (
          <div style={{ marginLeft: "5px", marginTop: "10px" }}>
            <h4>{bomTableTitleInfo?.category_name || BOM_table_for_step_title || ""}</h4>
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>
                    {partNumberColumnTitleInfo?.category_name || part_number_column_title || "Item Code"}
                  </th>
                  <th>Description</th>
                  <th>Qty Per</th>
                  <th>Unit</th>
                </tr>
              </thead>
              <tbody>
                {[...bomRows]
                  .sort((a, b) => {
                    // 1. Sort by Type orders field (ascending numerically)
                    const orderA = parseInt(
                      a.part_info?.type_info?.orders || 999999,
                      10
                    );
                    const orderB = parseInt(
                      b.part_info?.type_info?.orders || 999999,
                      10
                    );

                    if (orderA !== orderB) return orderA - orderB;

                    // 2. If type orders are equal, sort by part_number (numbers first, then alphabet)
                    const partNumberA = a.partNumber || "";
                    const partNumberB = b.partNumber || "";

                    // Helper to split into numeric and alpha
                    const splitPart = (str) => {
                      const match = str.match(/^(\d+)?([A-Za-z].*)?$/);
                      if (!match) return { num: NaN, rest: str };
                      return {
                        num: match[1] ? parseInt(match[1], 10) : NaN,
                        rest: match[2] || str,
                      };
                    };
                    const aSplit = splitPart(partNumberA);
                    const bSplit = splitPart(partNumberB);

                    // Numbers first
                    if (!isNaN(aSplit.num) && isNaN(bSplit.num)) return -1;
                    if (isNaN(aSplit.num) && !isNaN(bSplit.num)) return 1;
                    if (!isNaN(aSplit.num) && !isNaN(bSplit.num)) {
                      if (aSplit.num !== bSplit.num)
                        return aSplit.num - bSplit.num;
                      // If numbers equal, compare rest
                      return aSplit.rest.localeCompare(bSplit.rest);
                    }
                    // Both are alpha or both are NaN
                    return partNumberA.localeCompare(partNumberB);
                  })
                  .map((row, idx) => (
                    <tr key={`part-required-${idx}`}>
                      <td>{row.type || ""}</td>
                      <td>{row.partNumber || ""}</td>
                      <td>
                        <RenderHtml content={row.description} />
                      </td>
                      <td>{row.qty === 0 ? "" : row.qty}</td>
                      <td>{row.unit_name || ""}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Step-level Children (Rich Text, Video, Form, Canvas, Attachment) - exclude substeps */}
        {version_content.children && version_content.children.filter(c => c.type !== 'substep').length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <ChildrenRenderer
              children={version_content.children.filter(c => c.type !== 'substep')}
              parentContext={{
                sectionIndex: 0,
                stepIndex: 0,
                globalStepNumber: 1,
                sectionHeading: section || "Section",
                sectionId: version_content.section_id_ref,
                stepId: version_content.step_tree_node_id,
                partNumber: version_content.partNumber,
                revision: version_content.revision,
                partId: version_content.part_id_ref,
                revisionId: version_content.revision_id_ref,
              }}
              onNavigate={() => {}}
              checkboxState={{
                currentNodeId: version_content.step_tree_node_id,
                currentNodeType: "step",
                allChildrenChecked: true,
                allChildrenDisabled: true,
              }}
              disabled={true}
              allNodesData={[]}
            />
          </div>
        )}

        {/* Substeps */}
        {version_content.substeps && version_content.substeps.length > 0 && (
          <>
            {version_content.substeps.map((substep, substepIndex) => {
              const substepLetter = String.fromCharCode(65 + substepIndex); // A, B, C, ...
              const globalSubstepLabel = `Substep ${version_content.sibling_order || 1}${substepLetter}`;

              return (
                <div key={substep.sub_step_tree_node_id} style={{ marginTop: "20px" }}>
                  <div>
                    <label
                      style={{
                        display: "flex",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <input type="checkbox" disabled={true} checked={true} readOnly />
                      <h2 style={{ margin: 0 }}>{globalSubstepLabel}</h2>
                    </label>

                    <div style={{ marginLeft: "5px", whiteSpace: "pre-wrap" }}>
                      {substep.short_description}
                    </div>
                  </div>

                  {/* Substep Children */}
                  {substep.children && substep.children.length > 0 && (
                    <div style={{ marginTop: "10px" }}>
                      <ChildrenRenderer
                        children={substep.children}
                        parentContext={{
                          sectionIndex: 0,
                          stepIndex: 0,
                          substepIndex: substepIndex,
                          globalStepNumber: 1,
                          sectionHeading: section || "Section",
                          sectionId: version_content.section_id_ref,
                          stepId: version_content.step_tree_node_id,
                          substepId: substep.sub_step_tree_node_id,
                          partNumber: version_content.partNumber,
                          revision: version_content.revision,
                          partId: version_content.part_id_ref,
                          revisionId: version_content.revision_id_ref,
                        }}
                        onNavigate={() => {}}
                        checkboxState={{
                          currentNodeId: substep.sub_step_tree_node_id,
                          currentNodeType: "substep",
                          allChildrenChecked: true,
                          allChildrenDisabled: true,
                        }}
                        disabled={true}
                        allNodesData={[]}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default PreviewStep;
